import React from 'react';
import { observer } from 'mobx-react';
import Dimension from 'app/components/dimensions/Dimension';
import { FlexColumn, Tag, Tooltip, Box, Text } from 'core/components';
import { capitalizeFirstLetter } from 'app/util/utils';
import RunbookValueTagRenderer from 'app/components/runbooks/RunbookValueTagRenderer';

import {
  getPolicyDevices,
  getPolicyDeviceTag,
  getTopItems,
  getMetricOption,
  getNmsMetricOption,
  getPolicyMeasurementTag,
  getNmsDimensionOption,
  getDimensionOption
} from 'app/util/policies';

import {
  nmsMetricLabelRenderer,
  metricLabelRenderer,
  nmsDimensionLabelRenderer
} from 'app/components/metric/metricRenderers';

import NmsPolicyEntityTypeTag from './nms/NmsPolicyEntityTypeTag';

export const getDimensionTopItemRenderer = (dimension, model, reconDimensionOptions) => (
  <Box mr="3px" mb="3px" overflow="hidden">
    <Dimension
      dimension={dimension}
      round
      useChartTypes
      readOnly
      ellipsis
      useNmsDimensions={model.isMetricPolicy || model.isNmsPolicy ? reconDimensionOptions : undefined}
    />
  </Box>
);

export const policyStatusRenderer = ({ value, model }) => {
  const { policyError } = model;
  return (
    <FlexColumn alignItems="flex-start" width="100%" px="4px">
      {policyError ? (
        <Tooltip
          fill
          content={
            <Text as="div" style={{ maxWidth: 500 }}>
              {capitalizeFirstLetter(policyError.description)}
              .
              <br />
              {capitalizeFirstLetter(policyError.internal)}.
            </Text>
          }
        >
          <Tag intent="danger" width="100%" textAlign="center">
            Error
          </Tag>
        </Tooltip>
      ) : (
        <Tag intent={value ? 'success' : undefined} width="100%" textAlign="center">
          {value ? 'Enabled' : 'Disabled'}
        </Tag>
      )}
    </FlexColumn>
  );
};

export const policyNameRenderer = ({ model }) => {
  const description = model.get('description');
  return (
    <>
      <Text fontWeight="bold" ellipsis maxWidth="100%">
        {model.get('name')}
      </Text>
      {description && (
        <Text
          mt="4px"
          ellipsis
          small
          muted
          overflow="hidden"
          whiteSpace="normal"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: '2',
            WebkitBoxOrient: 'vertical'
          }}
        >
          {description}
        </Text>
      )}
    </>
  );
};

// Table cell renderers run outside an observer, so this component is what loads and reacts to the active device labels
const PolicyDevicesCell = observer(({ model }) => {
  const devices = getPolicyDevices({ model });

  return getTopItems({
    items: devices,
    max: 2,
    itemRenderer: (device) => getPolicyDeviceTag(device, { p: '2px 6px', mr: '3px', mb: '3px' }),
    tooltipItemRenderer: (device) => device?.label
  });
});

export const policyDataSourcesRenderer = ({ model, $metrics }) => {
  if (model.isMetricPolicy) {
    return getPolicyMeasurementTag(model.selectedMeasurement, {
      p: '2px 6px',
      mr: '3px',
      mb: '3px'
    });
  }

  if (model.isNmsPolicy) {
    return getTopItems({
      items: model.dataSources,
      max: 2,
      itemRenderer: (measurement) => getPolicyMeasurementTag(measurement, { p: '2px 6px', mr: '3px', mb: '3px' }),
      tooltipItemRenderer: (measurement) =>
        $metrics.availableLabelMeasurementOptions.find((m) => m.value === measurement)?.label
    });
  }

  if (model.get('selected_devices')) {
    return <PolicyDevicesCell model={model} />;
  }

  return null;
};

export const policyMetricsRenderer = ({ model }) => {
  const { isNmsPolicy, metrics } = model;
  // for NMS policies we can have multiple measurements (entity target type)
  const measurement = isNmsPolicy ? model.targetMeasurements : model.selectedMeasurement;
  const renderer = isNmsPolicy ? nmsMetricLabelRenderer : metricLabelRenderer;
  const tooltipItemRenderer = isNmsPolicy
    ? (metric) => getNmsMetricOption(metric, model.targetMeasurements)?.label
    : (metric) => getMetricOption(metric, model.selectedMeasurement)?.label;

  return metrics
    ? getTopItems({
        items: metrics,
        max: 2,
        itemRenderer: (metric, idx) =>
          renderer(metric, measurement, {
            round: true,
            fontWeight: 'medium',
            intent: idx === 0 ? 'primary' : undefined,
            p: '2px 6px'
          }),
        tooltipItemRenderer,
        column: true
      })
    : null;
};

export const policyDimensionsRenderer = ({ model, $metrics }) => {
  // NMS policies
  if (model.isNmsPolicy) {
    // Custom target
    if (model.target?.custom) {
      return getTopItems({
        items: model.dimensions,
        max: 2,
        itemRenderer: (dimension) =>
          nmsDimensionLabelRenderer(dimension, model.targetMeasurements, { intent: 'primary', round: true }),
        tooltipItemRenderer: (dimension) => getNmsDimensionOption(dimension, model.targetMeasurements)?.label
      });
    }

    // Entity type target
    if (model.entityType) {
      return <NmsPolicyEntityTypeTag entityType={model.entityType} />;
    }
  }

  // Standard policies
  const dimensions = model.get('dimensions');
  const measurement = model.selectedMeasurement;
  const reconDimensionOptions = $metrics.getAvailableFlowPolicyDimensionOptions(measurement);

  return dimensions
    ? getTopItems({
        items: dimensions,
        max: 2,
        itemRenderer: (dimension) => getDimensionTopItemRenderer(dimension, model, reconDimensionOptions),
        tooltipItemRenderer: (dimension) =>
          getDimensionOption(dimension, {
            reconDimensions: model.isMetricPolicy && reconDimensionOptions,
            useChartTypes: true
          })
      })
    : null;
};

export const policyUsedByTenantRenderer = ({ model, $mkp }) => {
  const match = $mkp.tenants.tenantSubpoliciesByPolicyId[model.id];

  if (!match) {
    return <Text muted>None</Text>;
  }

  return getTopItems({
    items: match,
    max: 2,
    itemRenderer: ({ tenant }) => (
      <Box mr="3px" overflow="hidden">
        {tenant.get('name')}
      </Box>
    ),
    tooltipItemRenderer: ({ tenant }) => <>{tenant.get('name')}</>
  });
};

const runbookItemRenderer = (rb) => (
  <RunbookValueTagRenderer runbooks={[{ id: rb.id, name: rb.get('name'), isPreset: rb.isPreset }]} />
);
const runbookTooltipItemRenderer = (rb) => <>{rb.get('name')}</>;

export const policyRunbooksRenderer = ({ model, $runbook }) =>
  getTopItems({
    items: $runbook.getRunbooksForPolicy(model),
    max: 2,
    itemRenderer: runbookItemRenderer,
    tooltipItemRenderer: runbookTooltipItemRenderer
  });
